/**
 * Session Recording Player - Replay user sessions
 * Uses rrweb-player for DOM replay
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Play, Clock, Calendar, Monitor } from 'lucide-react';

interface SessionRecording {
  id: string;
  session_id: string;
  user_id: string | null;
  anonymous_id: string | null;
  events: unknown[];
  started_at: string;
  ended_at: string | null;
  duration_ms: number;
  page_url: string;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  event_count: number;
}

export default function SessionRecording() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [playerReady, setPlayerReady] = useState(false);

  // Fetch session recording
  const { data: recording, isLoading, error } = useQuery({
    queryKey: ['session-recording', sessionId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('session_recordings')
        .select('*')
        .eq('session_id', sessionId)
        .single();
      if (error) throw error;
      return data as SessionRecording;
    },
    enabled: !!sessionId,
  });

  // Initialize rrweb player when recording loads
  useEffect(() => {
    if (!recording?.events || !playerContainerRef.current || playerRef.current) return;

    const initPlayer = async () => {
      try {
        // Dynamically import rrweb-player
        const rrwebPlayer = await import('rrweb-player');
        // Import styles
        await import('rrweb-player/dist/style.css');

        const events = Array.isArray(recording.events) ? recording.events : [];

        if (events.length === 0) {
          console.warn('No events to replay');
          return;
        }

        // Create player
        playerRef.current = new rrwebPlayer.default({
          target: playerContainerRef.current!,
          props: {
            events: events as any[],
            width: 800,
            height: 500,
            autoPlay: false,
            showController: true,
            speedOption: [1, 2, 4, 8],
          },
        });

        setPlayerReady(true);
      } catch (err) {
        console.error('Failed to initialize player:', err);
      }
    };

    initPlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current = null;
      }
    };
  }, [recording]);

  // Format duration
  const formatDuration = (ms: number) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Parse user agent for device info
  const getDeviceInfo = (userAgent: string) => {
    if (userAgent.includes('Mobile')) return 'Mobile';
    if (userAgent.includes('Tablet')) return 'Tablet';
    return 'Desktop';
  };

  // Get browser info
  const getBrowserInfo = (userAgent: string) => {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-[500px] w-full" />
        </div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/analytics')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Analytics
          </Button>
          <Card className="mt-6">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Recording not found or failed to load</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/analytics')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold">Session Recording</h1>
                <p className="text-sm text-muted-foreground font-mono">
                  {sessionId?.slice(0, 8)}...
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(recording.duration_ms)}
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <Monitor className="h-3 w-3" />
                {recording.screen_width}x{recording.screen_height}
              </Badge>
              <Badge variant="outline">
                {getDeviceInfo(recording.user_agent)} - {getBrowserInfo(recording.user_agent)}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Player */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Play className="h-4 w-4" />
                  Session Replay
                </CardTitle>
                <CardDescription>
                  All inputs are masked for FINRA compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  ref={playerContainerRef}
                  className="bg-muted rounded-lg overflow-hidden flex items-center justify-center min-h-[500px]"
                >
                  {!playerReady && (
                    <div className="text-center text-muted-foreground">
                      <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Loading player...</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Session Info Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Session Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground">Started</p>
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    {formatDate(recording.started_at)}
                  </p>
                </div>
                {recording.ended_at && (
                  <div>
                    <p className="text-xs text-muted-foreground">Ended</p>
                    <p className="text-sm font-medium">{formatDate(recording.ended_at)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm font-medium">{formatDuration(recording.duration_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Events Captured</p>
                  <p className="text-sm font-medium">{recording.event_count}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Page URL</p>
                  <p className="text-sm font-medium truncate" title={recording.page_url}>
                    {recording.page_url}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">User Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="text-sm font-mono">
                    {recording.user_id?.slice(0, 12) || 'Anonymous'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Device</p>
                  <p className="text-sm font-medium">{getDeviceInfo(recording.user_agent)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Browser</p>
                  <p className="text-sm font-medium">{getBrowserInfo(recording.user_agent)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Screen Size</p>
                  <p className="text-sm font-medium">{recording.screen_width} x {recording.screen_height}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
